import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ScrollIndicatorContainer = ({ children, sx = {} }) => {
  const [scrollPct, setScrollPct] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const scrollContainerRef = useRef(null);

  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      setHasOverflow(scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    
    if (!scrollContainerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });
    
    resizeObserver.observe(scrollContainerRef.current);
    
    return () => resizeObserver.disconnect();
  }, [children]);

  const handleScroll = (e) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.target;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll > 0) {
      setScrollPct((scrollLeft / maxScroll) * 100);
    }
  };

  const handleScrollClick = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ ...sx }}>
      {/* Cards Container */}
      <Box 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{ 
          display: 'flex', 
          flexWrap: 'nowrap', 
          gap: { xs: 1.5, lg: 2 },
          overflowX: 'auto',
          pt: 1,
          pb: 0.5,
          mb: 0,
          scrollbarWidth: 'none', // Hide default scrollbar in Firefox
          '&::-webkit-scrollbar': { 
            display: 'none' // Hide default scrollbar in Chrome/Safari
          }
        }}
      >
        {children}
      </Box>

      {/* Custom Scroll Indicator */}
      {hasOverflow && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 0, mt: -0.5, gap: 1 }}>
          <IconButton onClick={() => handleScrollClick('left')} size="small" sx={{ color: 'text.secondary' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          
          <Box sx={{ width: 60, height: 4, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 2, position: 'relative' }}>
            <Box sx={{ 
              width: 20, 
              height: 4, 
              bgcolor: 'primary.main', 
              borderRadius: 2, 
              position: 'absolute',
              left: `${scrollPct * 0.666}%`, 
              transition: 'left 0.1s ease'
            }} />
          </Box>

          <IconButton onClick={() => handleScrollClick('right')} size="small" sx={{ color: 'text.secondary' }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default ScrollIndicatorContainer;
